<template>
  <div class="fabric-images">
    <slot name="action-prefix" />
    <div v-if="compact" class="fabric-compact-image">
      <el-checkbox
        v-if="!replaceOnly"
        :value="value.action === 'replace'"
        :disabled="disabled || busy"
        @change="update({ action: $event ? 'replace' : 'keep' })"
        >修改水洗标/吊牌图</el-checkbox
      >
      <span v-else>统一设置水洗标/吊牌图</span>
      <button
        type="button"
        class="fabric-image-tile"
        aria-label="选择水洗标/吊牌图"
        :disabled="disabled || busy"
        @click="openPicker">
        <el-image v-if="previewUrl" :src="previewUrl" fit="cover"><span slot="error">加载失败</span></el-image>
        <span v-else>＋</span>
      </button>
    </div>
    <div v-else class="fabric-image-action-row">
      <el-select :value="value.action" :disabled="disabled" aria-label="图片操作" @change="update({ action: $event })">
        <el-option label="保留原图" value="keep" />
        <el-option label="替换水洗标/吊牌图" value="replace" />
        <el-option label="删除水洗标/吊牌图" value="delete" />
      </el-select>
      <el-button v-if="value.action === 'replace'" size="mini" :disabled="disabled || busy" @click="openPicker">
        {{ value.source_ref ? '更换图片' : '选择图片' }}
      </el-button>
    </div>
    <template v-if="value.action === 'replace'">
      <el-image
        v-if="previewUrl && !compact"
        class="fabric-preview-image"
        :src="previewUrl"
        :preview-src-list="[previewUrl]"
        fit="cover">
        <span slot="error">图片加载失败</span>
      </el-image>
      <small>{{ busy ? '处理中…' : '支持JPG/PNG、5MB以内，本地图片至少400×400；提交后仍须通过平台素材审核' }}</small>
      <el-checkbox :value="value.skip_existing" :disabled="disabled" @change="update({ skip_existing: $event })">
        若商品已有水洗标/吊牌图，则保留原图
      </el-checkbox>
    </template>
    <small v-if="value.action === 'delete'">必填图片不可删除；仅处理面料对应图片</small>
    <el-dialog
      title="选择水洗标/吊牌图片"
      :visible="pickerVisible"
      width="min(760px,94vw)"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="!busy"
      :before-close="closePicker"
      @closed="drafts = null">
      <div v-if="drafts" class="fabric-picker">
        <nav class="fabric-picker-tabs" aria-label="图片来源">
          <button
            v-for="tab in sourceTabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            :disabled="busy"
            @click="activeTab = tab.value">
            {{ tab.label }}
          </button>
        </nav>
        <div class="fabric-picker-body">
          <div v-show="activeTab === 'upload'">
            <el-upload
              drag
              action=""
              :multiple="false"
              :show-file-list="false"
              :http-request="upload"
              :disabled="busy"
              accept="image/jpeg,image/png"
              class="fabric-picker-upload">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                将图片拖到这里，或
                <em>点击上传</em>
              </div>
            </el-upload>
            <p class="fabric-picker-tip">仅支持JPG/PNG、5MB以内，图片宽高至少400像素。</p>
          </div>
          <div v-show="activeTab === 'url'">
            <el-input
              v-model="drafts.url.source_ref"
              type="textarea"
              :rows="4"
              placeholder="请输入HTTPS图片链接"
              :disabled="busy" />
            <p class="fabric-picker-tip">每次选择一张图片，仅支持HTTPS公网图片地址。</p>
          </div>
          <div v-show="activeTab === 'library'">
            <div class="fabric-picker-filters">
              <el-select v-model="libraryShop" :disabled="busy" placeholder="素材所属店铺" @change="load(1)">
                <el-option
                  v-for="shop in shops"
                  :key="shop.mer_shop_id"
                  :label="shop.shop_name"
                  :value="Number(shop.mer_shop_id)" />
              </el-select>
              <el-select v-model="folder" :disabled="busy" placeholder="素材分类" @change="load(1)">
                <el-option label="全部分类" value="0" />
                <el-option
                  v-for="item in folders"
                  :key="item.folder_id"
                  :label="item.folder_name || item.name"
                  :value="String(item.folder_id)" />
              </el-select>
              <el-button size="mini" :disabled="busy || !libraryShop" @click="load(1)">刷新图库</el-button>
              <el-input v-model="keyword" placeholder="搜索本页图片名称" :disabled="busy" />
            </div>
            <div class="fabric-library">
              <button
                v-for="item in filtered"
                :key="item.material_id"
                type="button"
                :disabled="busy"
                :class="{ selected: drafts.library.source_ref === String(item.material_id) }"
                @click="choose(item)">
                <el-image :src="item.byte_url" fit="cover" />
                <span>{{ item.material_name || item.name || item.material_id }}</span>
              </button>
            </div>
            <p v-if="!busy && !files.length" class="fabric-picker-tip">
              {{ galleryError || '暂无图片，请选择店铺和分类后刷新' }}
            </p>
            <div class="fabric-picker-pages">
              <el-button size="mini" :disabled="busy || page <= 1" @click="load(page - 1)">上一页</el-button>
              <span>第{{ page }}页</span>
              <el-button size="mini" :disabled="busy || page * 20 >= total" @click="load(page + 1)">下一页</el-button>
            </div>
          </div>
          <el-image
            v-if="draftPreview"
            class="fabric-picker-preview"
            :src="draftPreview"
            :preview-src-list="[draftPreview]"
            fit="contain">
            <span slot="error">图片加载失败</span>
          </el-image>
          <p class="fabric-picker-tip">{{ busy ? '处理中…' : '确定后回填图片；批次执行时仍须通过抖店素材审核。' }}</p>
        </div>
      </div>
      <span slot="footer">
        <el-button size="mini" :disabled="busy" @click="closePicker">取消</el-button>
        <el-button size="mini" type="primary" :disabled="busy || !draftPreview" @click="confirmPicker">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import { getDoudianFabricMaterials, uploadDoudianFabricSource } from '@/api/main/doudianBatchEdit'
export default {
  props: {
    value: { type: Object, required: true },
    shops: { type: Array, default: () => [] },
    disabled: Boolean,
    compact: Boolean,
    replaceOnly: Boolean
  },
  data: () => ({
    busy: false,
    pickerVisible: false,
    activeTab: 'upload',
    drafts: null,
    sourceTabs: [
      { value: 'upload', label: '手动上传' },
      { value: 'url', label: '输入图片链接' },
      { value: 'library', label: '图库选择' }
    ],
    libraryShop: '',
    folder: '0',
    folders: [],
    files: [],
    keyword: '',
    page: 1,
    total: 0,
    galleryError: '',
    request: 0
  }),
  computed: {
    filtered() {
      return this.files.filter((item) => String(item.material_name || item.name || '').includes(this.keyword))
    },
    previewUrl() {
      return this.value.source_kind === 'library' ? this.value.source_url : this.value.source_ref
    },
    draftPreview() {
      const draft = this.drafts?.[this.activeTab]
      return draft ? (draft.source_kind === 'library' ? draft.source_url : draft.source_ref) : ''
    }
  },
  beforeDestroy() {
    this.request++
  },
  methods: {
    update(patch) {
      this.$emit('input', { ...this.value, ...patch })
    },
    openPicker() {
      this.drafts = Object.fromEntries(
        this.sourceTabs.map((tab) => [
          tab.value,
          { source_kind: tab.value, source_ref: '', source_shop_id: undefined, source_url: undefined }
        ])
      )
      const kind = this.value.source_kind
      if (this.value.source_ref && this.drafts[kind]) {
        this.drafts[kind] = {
          ...this.drafts[kind],
          source_ref: this.value.source_ref,
          source_shop_id: this.value.source_shop_id,
          source_url: this.value.source_url
        }
        this.activeTab = kind
      } else this.activeTab = 'upload'
      this.pickerVisible = true
    },
    closePicker() {
      if (!this.busy) this.pickerVisible = false
    },
    confirmPicker() {
      const draft = { ...this.drafts[this.activeTab], source_ref: this.drafts[this.activeTab].source_ref.trim() }
      if (!draft.source_ref) return this.$message.warning('请上传图片或填写图片链接')
      if (draft.source_kind === 'url') {
        try {
          const url = new URL(draft.source_ref)
          if (
            url.protocol !== 'https:' ||
            url.username ||
            url.password ||
            url.hash ||
            url.port ||
            !url.hostname.includes('.') ||
            /^[\d.]+$/.test(url.hostname) ||
            /\.(local|localhost)$/.test(url.hostname)
          )
            throw new Error()
        } catch {
          return this.$message.warning('请输入有效的HTTPS公网图片链接')
        }
      }
      this.update(this.compact ? { ...draft, action: 'replace' } : draft)
      this.pickerVisible = false
    },
    async upload(options) {
      const file = options.file
      if (this.busy) return
      if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) {
        const error = new Error('仅支持5MB以内的JPG/PNG图片')
        options.onError(error)
        return this.$message.warning(error.message)
      }
      this.busy = true
      this.$emit('busy', true)
      try {
        const dimensions = await new Promise((resolve, reject) => {
          const img = new Image(),
            url = URL.createObjectURL(file)
          img.onload = () => {
            URL.revokeObjectURL(url)
            resolve([img.width, img.height])
          }
          img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('图片无法读取'))
          }
          img.src = url
        })
        if (dimensions[0] < 400 || dimensions[1] < 400) throw new Error('图片宽高至少400像素')
        const result = await uploadDoudianFabricSource(file)
        const url = result.url || result.address
        if (!url) throw new Error('图片上传未返回有效地址')
        if (!this._isDestroyed && this.drafts) {
          this.drafts.upload.source_ref = url
          options.onSuccess(result)
        }
      } catch (error) {
        options.onError(error)
        if (!this._isDestroyed) this.$message.error(error.message || '图片上传失败')
      } finally {
        this.busy = false
        this.$emit('busy', false)
      }
    },
    async load(page) {
      if (!this.libraryShop || this.busy) return
      const seq = ++this.request
      this.busy = true
      this.$emit('busy', true)
      this.galleryError = ''
      try {
        const response = await getDoudianFabricMaterials({
          shop_id: Number(this.libraryShop),
          folder_id: this.folder,
          page,
          limit: 20
        })
        if (seq !== this.request || this._isDestroyed) return
        const data = response.data,
          info = data.files.folder_info || {}
        this.folders = data.folders.data?.folder_info_list || []
        this.files = (info.child_material || []).filter((item) => item.byte_url && item.material_id)
        this.total = Number(info.total_child_material_num || 0)
        this.page = page
      } catch (error) {
        if (seq === this.request && !this._isDestroyed) {
          this.files = []
          this.galleryError = error.message || '图库读取失败'
          this.$message.error(this.galleryError)
        }
      } finally {
        if (seq === this.request) {
          this.busy = false
          this.$emit('busy', false)
        }
      }
    },
    choose(item) {
      this.drafts.library = {
        source_kind: 'library',
        source_ref: String(item.material_id),
        source_shop_id: Number(this.libraryShop),
        source_url: item.byte_url
      }
    }
  }
}
</script>
<style scoped>
.fabric-images {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  min-width: 0;
  font-size: 12px;
}
.fabric-compact-image {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.fabric-image-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #909399;
  font-size: 24px;
  cursor: pointer;
}
.fabric-image-tile .el-image {
  width: 62px;
  height: 62px;
  font-size: 11px;
}
.fabric-images > .el-select {
  width: 280px;
  max-width: 100%;
}
.fabric-images small {
  font-size: 10px;
  color: #888;
  white-space: normal;
}
.fabric-preview-image {
  width: 78px;
  height: 78px;
}
.fabric-images ::v-deep .el-input__inner {
  height: 30px;
  line-height: 30px;
  font-size: 11px;
}
.fabric-images ::v-deep .el-checkbox__label {
  font-size: 12px;
  white-space: normal;
}
</style>
<style scoped>
.fabric-image-action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
}
.fabric-image-action-row > .el-checkbox {
  margin-right: 0;
}
.fabric-image-action-row > .el-select {
  flex: 1 1 160px;
  width: 280px;
  max-width: 280px;
  min-width: 0;
}
</style>
<style scoped>
.fabric-picker {
  font-size: 12px;
}
.fabric-picker-tabs {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e8e8e8;
  flex-wrap: wrap;
}
.fabric-picker-tabs button {
  padding: 12px 20px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #666;
  cursor: pointer;
  font-size: 12px;
}
.fabric-picker-tabs button.active {
  color: #c8443c;
  border-bottom-color: #c8443c;
}
.fabric-picker-body {
  padding: 24px 0;
  min-height: 260px;
  max-height: 55vh;
  overflow: auto;
}
.fabric-picker-tip {
  color: #999;
  font-size: 11px;
  line-height: 20px;
}
.fabric-picker-preview {
  display: block;
  width: 120px;
  height: 120px;
  margin-top: 16px;
}
.fabric-picker-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.fabric-picker-filters > .el-select,
.fabric-picker-filters > .el-input {
  width: 180px;
  max-width: 100%;
}
.fabric-library {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.fabric-library button {
  width: 90px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  cursor: pointer;
}
.fabric-library .el-image {
  width: 70px;
  height: 70px;
}
.fabric-library span {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fabric-library .selected {
  border-color: #c8443c;
}
.fabric-picker-pages {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.fabric-picker-upload ::v-deep .el-upload,
.fabric-picker-upload ::v-deep .el-upload-dragger {
  width: 100%;
  max-width: 100%;
}
.fabric-picker-tabs button:disabled,
.fabric-library button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
